import React from 'react';
import { useTranslation } from 'react-i18next';
import useRevealOnScroll from '../../hooks/useRevealOnScroll';
import BackButton from '../../components/BackButton';

import MTP3000 from '../../images/radio/MTP3000.png';
import MTP8000ex from '../../images/radio/MTP8000ex.png';
import MXM600 from '../../images/radio/MXM600.webp';
import MXP600 from '../../images/radio/MXP600.png';
import MXP660 from '../../images/radio/MXP660.webp';
import MXP7000 from '../../images/radio/MXP7000.png';
import TPG2200 from '../../images/radio/TPG2200.png';

const tints = ['0,194,255', '0,112,210', '163,230,53'];

function ItemRow({ idx, item, reversed, tint }) {
  const [revealRef, isVisible] = useRevealOnScroll(0.1);

  const handleSpotlight = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--x', `${((e.clientX - rect.left) / rect.width) * 100}%`);
    e.currentTarget.style.setProperty('--y', `${((e.clientY - rect.top) / rect.height) * 100}%`);
  };

  return (
    <div
      ref={revealRef}
      className={`reveal group flex flex-col md:items-center gap-8 md:gap-16 py-14 sm:py-16 ${
        reversed ? 'md:flex-row-reverse' : 'md:flex-row'
      } ${isVisible ? 'is-visible' : ''}`}
    >
      <div className="flex-1 min-w-0">
        <span
          className="font-display font-extrabold text-white/[0.04] block leading-none select-none text-[96px] sm:text-[120px] -mb-8 sm:-mb-10"
          aria-hidden="true"
        >
          {String(idx + 1).padStart(2, '0')}
        </span>
        <h3 className="font-display text-2xl sm:text-3xl font-bold text-white mb-4 relative">
          {item.name}
        </h3>
        <p className="text-white/60 leading-relaxed max-w-lg relative">
          {item.description}
        </p>
      </div>

      <div
        onMouseMove={handleSpotlight}
        className="spotlight glass-card flex-none w-full md:w-[380px] h-[220px] sm:h-[260px] rounded-3xl relative overflow-hidden transition-colors duration-300 group-hover:border-cyan-400/40 flex items-center justify-center"
      >
        <img
          src={item.image}
          alt={item.name}
          className="relative z-10 max-h-[70%] max-w-[80%] object-contain group-hover:scale-105 transition-transform duration-500"
        />
        <div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(circle at ${reversed ? '70%' : '30%'} 25%, rgba(${tint},0.2), transparent 60%)`,
          }}
        />
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="mb-2">
      <h2 className="font-display text-2xl sm:text-3xl font-bold text-white">{children}</h2>
      <span className="title-accent is-visible" />
    </div>
  );
}

export default function RadioPage() {
  const { t } = useTranslation('radio');

  const portable = [
    { key: 'mxp600', image: MXP600 },
    { key: 'mxp660', image: MXP660 },
    { key: 'mxp7000', image: MXP7000 },
    { key: 'mtp8000ex', image: MTP8000ex },
    { key: 'mtp3000', image: MTP3000 },
    { key: 'tpg2200', image: TPG2200 },
  ].map(({ key, image }) => ({
    name: t(`items.portable.${key}.name`),
    description: t(`items.portable.${key}.description`),
    image,
  }));

  const mobile = [
    { key: 'mxm600', image: MXM600 },
  ].map(({ key, image }) => ({
    name: t(`items.mobile.${key}.name`),
    description: t(`items.mobile.${key}.description`),
    image,
  }));

  return (
    <section className="relative min-h-screen text-white bg-gradient-to-b from-[#070b15] to-[#0a111f] pt-32 pb-24 overflow-hidden">
      <div className="grid-bg opacity-60" />
      <div className="glow-orb w-[560px] h-[560px] bg-[#0070d2] opacity-20 -top-40 -left-32 [animation:drift-slow_24s_ease-in-out_infinite,glow-pulse_9s_ease-in-out_infinite]" />
      <div className="glow-orb w-[480px] h-[480px] bg-[#00c2ff] opacity-20 top-1/3 right-[-160px] [animation:drift_15s_ease-in-out_infinite_2s,glow-pulse_9s_ease-in-out_infinite_1.5s]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <BackButton label={t('back')} to="/" hash="#services" />

        <div className="max-w-2xl">
          <p className="font-mono text-xs tracking-[0.3em] text-white/40 mb-2">
            01&ndash;{String(portable.length + mobile.length).padStart(2, '0')}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-white">{t('title')}</h1>
          <span className="title-accent is-visible" />
          <p className="text-white/60 text-lg mt-6">{t('subtitle')}</p>
        </div>

        <div className="mt-16">
          <SectionTitle>{t('portable_title')}</SectionTitle>
          <div className="divide-y divide-white/10">
            {portable.map((item, idx) => (
              <ItemRow key={item.name} idx={idx} item={item} reversed={idx % 2 === 1} tint={tints[idx % tints.length]} />
            ))}
          </div>
        </div>

        <div className="mt-8">
          <SectionTitle>{t('mobile_title')}</SectionTitle>
          <div className="divide-y divide-white/10">
            {mobile.map((item, idx) => (
              <ItemRow
                key={item.name}
                idx={portable.length + idx}
                item={item}
                reversed={idx % 2 === 1}
                tint={tints[idx % tints.length]}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
